import { FC, useMemo, useState } from "react";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccordionDetails from "@mui/material/AccordionDetails";
import Accordion from "@mui/material/Accordion";
import { SizeMeasurement } from "@/utils/inspection.utils.ts";
import {
    MeasurementPass,
} from "@/features/inspection-details/components/record-measurement/components/MeasurementPass.tsx";
import {
    MeasurementRecordRow
} from "@/features/inspection-details/components/record-measurement/components/MeasurementRecordRow.tsx";

import styles
    from "@/features/inspection-details/components/record-measurement/components/styles/MeasurementSize.module.scss";
import { useTranslation } from "react-i18next";


interface MeasurementSizeProps {
    sizeKey: string;
    measurements: SizeMeasurement[];
    handleCardToggle: (id: string | null) => void;
    readOnly?: boolean;
    showDefectColumn?: boolean;
    handleMeasurementChange: (
        sizeKey: string,
        value: number | null,
        sizeChartId: string,
        sampleNo: number,
        measurementId: string | null
    ) => void;
    idx: string;
    expandedCard: string | null;
    onAdd: () => void
}

export const MeasurementSize: FC<MeasurementSizeProps> = ({
    sizeKey,
    measurements,
    handleCardToggle,
    readOnly = false,
    showDefectColumn = true,
    idx,
    expandedCard,
    handleMeasurementChange,
    onAdd
}) => {

    const { t: translate } = useTranslation();
    const [selectedPass, setSelectedPass] = useState(1);

    function handleInputChange(
        sizeKey: string,
        value: number | null,
        sizeChartId: string,
        inspectionId: string | null
    ) {
        handleMeasurementChange(
            sizeKey,
            value,
            sizeChartId,
            selectedPass,
            inspectionId
        )
    }
    const selectedMeasurements = useMemo(() => {
        return measurements.filter(m => m.sampleNumber === selectedPass);
    }, [measurements, selectedPass]);
    return (
        <Accordion
            expanded={expandedCard === idx.toString()}
            onChange={() => handleCardToggle(idx)}
            disableGutters
            sx={{
                margin: 0,
                padding: 0,
                boxShadow: 'none',
                borderRadius: '5px',
                '&:before': {
                    display: 'none',
                },
                '&.Mui-expanded': {
                    marginBottom: 0,
                    marginTop: 0
                }
            }}

            key={idx} className={styles.accordion}>
            <AccordionSummary
                key={idx}
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel${idx}-content`}
                id={`panel${idx}-header`}
                className={styles.accordionSummary}
                sx={{
                    borderBottom: '1px solid #ECECEB',
                    '&.Mui-expanded': {
                        borderColor: '#0C63E4',
                        margin: 0,
                        color: '#0C63E4'
                    },
                    '&.Mui-expanded .MuiAccordionSummary-expandIconWrapper': {
                        color: '#0C63E4',
                    }
                }}
            >
                <div className={styles.summaryContent}>
                    <p className={styles.sizeText}>Size</p>
                    <p className={styles.sizeHeader}>{sizeKey}</p>
                </div>
            </AccordionSummary>
            {expandedCard === idx.toString() && <AccordionDetails className={styles.accordionDetails} >
                <MeasurementPass key={`pass-${idx}`} selectedPass={selectedPass}
                    updateSelectedPass={(selectedPass) => setSelectedPass(selectedPass)} />
                <div key={`data-${idx}`} className={styles.tableContainer}>
                    <div className={styles.tableGrid}>
                        {/* Header Row */}
                        <div className={`${styles.header} ${!showDefectColumn ? styles.headerNoDefect : ''}`}>
                            <div className={styles.headerCell}>{translate('Tgt')}</div>
                            <div className={styles.headerCell}>{translate('Tol -')}</div>
                            <div className={styles.headerCell}>{translate('Tol +')}</div>
                            <div className={styles.headerCell}>{translate('Variance')}</div>
                            <div className={styles.headerCell}>{translate('Measure')}</div>
                            {showDefectColumn && (
                                <div className={`${styles.headerCell} ${styles.defectCell}`}>{translate('Defect')}</div>
                            )}
                        </div>

                        {/* Data Rows */}
                        {selectedMeasurements.map((row, rowIndex) => (
                            <MeasurementRecordRow
                                passIndex={idx}
                                key={`Size_${sizeKey}-pass_${idx}-measurement_${rowIndex}`}
                                measurement={row}
                                measurementIndex={rowIndex}
                                sizeKey={sizeKey}
                                handleInputChange={handleInputChange}
                                onAdd={onAdd}
                                readOnly={readOnly}
                                showDefectColumn={showDefectColumn}
                            />


                        ))}
                    </div>
                </div>
            </AccordionDetails>}
        </Accordion>
    )
}